import {
  nextTick,
  onMounted,
  onUnmounted,
  ref,
  watch,
  type Ref,
} from "vue";

/**
 * Отслеживает вертикальное переполнение контейнера (появился ли скролл).
 *
 * Нужен, чтобы показывать кнопку «на весь экран» только тогда, когда
 * содержимое блока превышает его max-height и внутри появляется скролл.
 *
 * Тело блока обычно рендерится под v-if, поэтому элемент появляется позже —
 * наблюдатель (пере)подключается через watch на сам ref. Изменение данных
 * (например, длины списка) не меняет размер контейнера, а значит не вызывает
 * ResizeObserver — для этого передаётся deps-триггер, по которому делаем
 * повторный замер.
 *
 * @param elRef ref на скролл-контейнер (может быть null)
 * @param deps  функция-триггер (напр. () => list.length) для пересчёта при смене данных
 */
export function useOverflowDetection(
  elRef: Ref<HTMLElement | null>,
  deps?: () => unknown,
) {
  const isOverflowing = ref(false);

  let observer: ResizeObserver | null = null;
  // Защита от петли ResizeObserver: не измеряем повторно в том же кадре.
  let measuring = false;

  const measure = () => {
    const el = elRef.value;
    if (!el) {
      isOverflowing.value = false;
      return;
    }
    if (measuring) {
      return;
    }
    measuring = true;
    isOverflowing.value = el.scrollHeight > el.clientHeight + 1;
    requestAnimationFrame(() => {
      measuring = false;
    });
  };

  const connect = (el: HTMLElement | null) => {
    observer?.disconnect();
    observer = null;
    if (!el) {
      isOverflowing.value = false;
      return;
    }
    observer = new ResizeObserver(() => measure());
    observer.observe(el);
    measure();
  };

  onMounted(() => {
    nextTick(() => connect(elRef.value));
  });

  watch(elRef, (el) => connect(el));

  if (deps) {
    watch(deps, () => nextTick(measure));
  }

  onUnmounted(() => {
    observer?.disconnect();
    observer = null;
  });

  return { isOverflowing };
}
